import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';

/**
 * Live quotes are left alone: refetching one could re-price the stay a guest is booking. Both
 * kinds: a checkout's by code (`/live_quotes/<code>`) and a rental page's for the chosen dates
 * (`['liveQuote', …]`, which POSTs a new quote).
 */
export const isLiveQuoteKey = (queryKey: readonly unknown[]): boolean =>
  queryKey[0] === 'liveQuote' || (typeof queryKey[0] === 'string' && queryKey[0].startsWith('/live_quotes/'));

/**
 * The signed-in customer changed (signed in here, or in another tab): the page updates in place,
 * without a reload, so the customer stays where they were with what they typed (a booking in
 * progress keeps its form). The router reads the session again (`getSession` in the root's
 * `beforeLoad`: the navbar, the checkout and the account pages follow `currentUser`) while the data
 * that depends on who is asking refetches: saved-rental hearts, the wishlist, the profile, trips.
 */
export function useRefreshSession(): () => Promise<void> {
  const router = useRouter();
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await Promise.all([
      router.invalidate(),
      queryClient.invalidateQueries({ predicate: (query) => !isLiveQuoteKey(query.queryKey) }),
    ]);
  }, [queryClient, router]);
}
